/**
 * MilestoneTracker — detects locked→unlocked transitions across polls.
 *
 * Port of apps/signage-tv/lib/rendering/milestone_tracker.dart. Kept pure
 * (no DOM, no timers) so it's trivially unit-testable and safe to call
 * from any render pass.
 *
 * Spec: signage-device-app-spec §3.2
 */
export class MilestoneTracker {
  private readonly unlocked = new Set<string>();

  /**
   * Returns milestone presentation data that transitioned from
   * locked/unknown to unlocked since the previous call. Idempotent — calling
   * with the same input twice returns an empty second result.
   */
  diff(milestones: ReadonlyArray<Record<string, unknown>>): UnlockedMilestone[] {
    const newly: UnlockedMilestone[] = [];
    for (const m of milestones) {
      const title = typeof m['title'] === 'string' ? (m['title'] as string).trim() : '';
      if (!title) continue;
      const isUnlocked = m['unlocked'] === true;
      if (isUnlocked && !this.unlocked.has(title)) {
        const rawAmount = m['amount_minor_units'] ?? m['amount'];
        const parsedAmount = Number(rawAmount ?? 0);
        newly.push({
          title,
          amountMinorUnits: Number.isFinite(parsedAmount)
            ? Math.max(0, Math.trunc(parsedAmount))
            : 0,
        });
        this.unlocked.add(title);
      }
    }
    return newly;
  }

  /**
   * Seed the tracker from an initial snapshot without emitting celebrations.
   * Called after the first successful content fetch so a device that boots
   * mid-session doesn't replay every past unlock.
   */
  prime(milestones: ReadonlyArray<Record<string, unknown>>): void {
    for (const m of milestones) {
      const title = typeof m['title'] === 'string' ? (m['title'] as string).trim() : '';
      if (!title) continue;
      if (m['unlocked'] === true) this.unlocked.add(title);
    }
  }

  get isPrimed(): boolean {
    return this.unlocked.size > 0;
  }

  get unlockedCount(): number {
    return this.unlocked.size;
  }
}

export interface UnlockedMilestone {
  title: string;
  amountMinorUnits: number;
}
